// Loop detection and normalization adapted from NovelSprint's working sound player.
export type NoiseType = 'brown' | 'pink' | 'underwater' | 'campfire' | 'soaking' | 'water' | 'cafe';
type LoopPoints = {start:number;end:number};
const LOOP_SILENCE_THRESHOLD = 0.0008;
const MIN_LOOP_WINDOW_SECONDS = 1.2;
const AMBIENCE_MIN_LOOP_RATIO = 0.62;
const AMBIENCE_MIN_LOOP_SECONDS = 8;
const LOOP_INTRO_SKIP_SECONDS = 3.0;
const BROWN_LOOP_INTRO_SKIP_SECONDS = 3.6;
const UNDERWATER_LOOP_INTRO_SKIP_SECONDS = 4.2;
const LOOP_ZERO_CROSSING_SEARCH_SECONDS = 0.18;
const LOOP_MATCH_WINDOW_SECONDS = 0.07;
const LOOP_MATCH_SEARCH_STEP_SAMPLES = 160;
const BROWN_LOOP_ZERO_CROSSING_SEARCH_SECONDS = 0.24;
const BROWN_LOOP_MATCH_WINDOW_SECONDS = 0.11;
const BROWN_LOOP_MATCH_SEARCH_STEP_SAMPLES = 112;
const UNDERWATER_LOOP_ZERO_CROSSING_SEARCH_SECONDS = 0.28;
const UNDERWATER_LOOP_MATCH_WINDOW_SECONDS = 0.13;
const UNDERWATER_LOOP_MATCH_SEARCH_STEP_SAMPLES = 96;
const DEFAULT_LOOP_CROSSFADE_SECONDS = 0.45;
const DEFAULT_LOOP_MAX_CROSSFADE_SECONDS = 0.8;
const DEFAULT_LOOP_MIN_STEP_SECONDS = 0.25;
const BROWN_LOOP_CROSSFADE_SECONDS = 0.24;
const BROWN_LOOP_MIN_STEP_SECONDS = 0.34;
const UNDERWATER_LOOP_CROSSFADE_SECONDS = 0.65;
const UNDERWATER_LOOP_MIN_STEP_SECONDS = 0.42;
const LOOP_SCHEDULER_TICK_MS = 40;
const LOOP_SCHEDULER_LOOKAHEAD_SECONDS = 0.95;
const DEFAULT_VOICE_ATTACK_SECONDS = 0.04;
const BROWN_VOICE_ATTACK_SECONDS = 0.08;
const UNDERWATER_VOICE_ATTACK_SECONDS = 0.12;
const getLoopIntroSkipSeconds = (type: NoiseType): number => {
  if (type === 'underwater') return UNDERWATER_LOOP_INTRO_SKIP_SECONDS;
  if (type === 'brown') return BROWN_LOOP_INTRO_SKIP_SECONDS;
  return LOOP_INTRO_SKIP_SECONDS;
};

const getLoopMatchWindowSeconds = (type: NoiseType): number => {
  if (type === 'brown') return BROWN_LOOP_MATCH_WINDOW_SECONDS;
  if (type === 'underwater') return UNDERWATER_LOOP_MATCH_WINDOW_SECONDS;
  return LOOP_MATCH_WINDOW_SECONDS;
};

const getLoopMatchSearchStepSamples = (type: NoiseType): number => {
  if (type === 'brown') return BROWN_LOOP_MATCH_SEARCH_STEP_SAMPLES;
  if (type === 'underwater') return UNDERWATER_LOOP_MATCH_SEARCH_STEP_SAMPLES;
  return LOOP_MATCH_SEARCH_STEP_SAMPLES;
};

const getLoopZeroCrossingSearchSeconds = (type: NoiseType): number => {
  if (type === 'brown') return BROWN_LOOP_ZERO_CROSSING_SEARCH_SECONDS;
  if (type === 'underwater') return UNDERWATER_LOOP_ZERO_CROSSING_SEARCH_SECONDS;
  return LOOP_ZERO_CROSSING_SEARCH_SECONDS;
};

const getVoiceAttackSeconds = (type: NoiseType): number => {
  if (type === 'underwater') return UNDERWATER_VOICE_ATTACK_SECONDS;
  if (type === 'brown') return BROWN_VOICE_ATTACK_SECONDS;
  return DEFAULT_VOICE_ATTACK_SECONDS;
};

const getMinimumLoopSeconds = (audioBuffer: AudioBuffer, type: NoiseType): number => {
  if (type === 'brown' || type === 'underwater') return MIN_LOOP_WINDOW_SECONDS;

  const availableAfterIntro = Math.max(
    MIN_LOOP_WINDOW_SECONDS,
    audioBuffer.duration - getLoopIntroSkipSeconds(type) - 1
  );
  return Math.min(
    availableAfterIntro,
    Math.max(AMBIENCE_MIN_LOOP_SECONDS, audioBuffer.duration * AMBIENCE_MIN_LOOP_RATIO)
  );
};

const getLoopTailReserveSeconds = (type: NoiseType): number => {
  if (type === 'brown') return 0.4;
  if (type === 'underwater') return 0.9;
  return 1;
};

const findNearestZeroCrossing = (
  data: Float32Array,
  targetSample: number,
  minSample: number,
  maxSample: number,
  radiusSamples: number
): number => {
  const lo = Math.max(minSample, targetSample - radiusSamples);
  const hi = Math.min(maxSample, targetSample + radiusSamples);
  if (hi <= lo) return Math.max(minSample, Math.min(maxSample, targetSample));

  let bestSample = Math.max(minSample, Math.min(maxSample, targetSample));
  let bestCost = Number.POSITIVE_INFINITY;
  let foundCrossing = false;

  const scoreCandidate = (index: number, prev: number, curr: number): number => {
    const amplitudeCost = Math.abs(prev) + Math.abs(curr);
    const slopeCost = Math.abs(curr - prev);
    const distanceCost = Math.abs(index - targetSample) * 0.00008;
    return amplitudeCost + slopeCost * 1.2 + distanceCost;
  };

  for (let i = lo + 1; i <= hi; i++) {
    const prev = data[i - 1] ?? 0;
    const curr = data[i] ?? 0;
    if ((prev <= 0 && curr >= 0) || (prev >= 0 && curr <= 0)) {
      const cost = scoreCandidate(i, prev, curr);
      if (cost < bestCost) {
        bestCost = cost;
        bestSample = i;
      }
      foundCrossing = true;
      continue;
    }

    if (foundCrossing) continue;
    const cost = scoreCandidate(i, prev, curr);
    if (cost < bestCost) {
      bestCost = cost;
      bestSample = i;
    }
  }

  return bestSample;
};

const computeLoopBoundaryScore = (
  data: Float32Array,
  startSample: number,
  endSample: number,
  windowSamples: number
): number => {
  if (windowSamples <= 0) return Number.POSITIVE_INFINITY;
  if (startSample + windowSamples >= data.length) return Number.POSITIVE_INFINITY;
  const endWindowStart = endSample - windowSamples;
  if (endWindowStart < 0 || endWindowStart >= data.length) return Number.POSITIVE_INFINITY;

  let diffSum = 0;
  let slopeDiffSum = 0;
  for (let i = 0; i < windowSamples; i++) {
    const a = data[startSample + i] ?? 0;
    const b = data[endWindowStart + i] ?? 0;
    const diff = a - b;
    diffSum += diff * diff;

    const aPrev = data[Math.max(0, startSample + i - 1)] ?? 0;
    const bPrev = data[Math.max(0, endWindowStart + i - 1)] ?? 0;
    const slopeDiff = (a - aPrev) - (b - bPrev);
    slopeDiffSum += slopeDiff * slopeDiff;
  }

  const edgeA = data[startSample] ?? 0;
  const edgeB = data[endSample - 1] ?? 0;
  const edgeDiff = edgeA - edgeB;
  const edgeAbs = Math.abs(edgeA) + Math.abs(edgeB);
  const edgeSlopeA = edgeA - (data[Math.max(0, startSample - 1)] ?? 0);
  const edgeSlopeB = (data[Math.min(data.length - 1, endSample)] ?? edgeB) - edgeB;
  const edgeSlopeDiff = edgeSlopeA - edgeSlopeB;
  const edgeSlopeAbs = Math.abs(edgeSlopeA) + Math.abs(edgeSlopeB);

  return (
    diffSum / windowSamples +
    slopeDiffSum / windowSamples * 1.8 +
    edgeDiff * edgeDiff * 6 +
    edgeSlopeDiff * edgeSlopeDiff * 9 +
    edgeAbs * edgeAbs * 2.2 +
    edgeSlopeAbs * edgeSlopeAbs * 4.4
  );
};

const detectLoopPoints = (audioBuffer: AudioBuffer, type: NoiseType): LoopPoints | null => {
  const channelData = audioBuffer.getChannelData(0);
  const sampleRate = audioBuffer.sampleRate;
  const minLoopSamples = Math.floor(getMinimumLoopSeconds(audioBuffer, type) * sampleRate);

  let firstAudibleSample = -1;
  let lastAudibleSample = -1;

  for (let i = 0; i < channelData.length; i++) {
    if (Math.abs(channelData[i] ?? 0) > LOOP_SILENCE_THRESHOLD) {
      firstAudibleSample = i;
      break;
    }
  }

  for (let i = channelData.length - 1; i >= 0; i--) {
    if (Math.abs(channelData[i] ?? 0) > LOOP_SILENCE_THRESHOLD) {
      lastAudibleSample = i;
      break;
    }
  }

  if (firstAudibleSample < 0 || lastAudibleSample <= firstAudibleSample) {
    return null;
  }

  const tailReserveSamples = Math.floor(getLoopTailReserveSeconds(type) * sampleRate);
  const lastUsableSample = Math.max(firstAudibleSample, lastAudibleSample - tailReserveSamples);
  const availableSamples = lastUsableSample - firstAudibleSample;
  if (availableSamples < minLoopSamples) {
    return null;
  }

  // Skip the first seconds of the audible region to avoid audible intro->loop mismatch.
  const introSkipSamples = Math.floor(getLoopIntroSkipSeconds(type) * sampleRate);
  const maxStartBeforeMinWindow = lastUsableSample - minLoopSamples;
  const adjustedStartSample = Math.max(
    firstAudibleSample,
    Math.min(firstAudibleSample + introSkipSamples, maxStartBeforeMinWindow)
  );

  const loopMatchWindowSamples = Math.max(64, Math.floor(getLoopMatchWindowSeconds(type) * sampleRate));
  const searchStep = Math.max(48, getLoopMatchSearchStepSamples(type));
  const searchStart = Math.max(adjustedStartSample + minLoopSamples, adjustedStartSample + loopMatchWindowSamples + 1);
  const searchEnd = lastUsableSample;

  let bestEndSample = searchEnd;
  let bestScore = computeLoopBoundaryScore(channelData, adjustedStartSample, searchEnd, loopMatchWindowSamples);

  if (searchStart < searchEnd) {
    for (let candidate = searchStart; candidate <= searchEnd; candidate += searchStep) {
      const score = computeLoopBoundaryScore(channelData, adjustedStartSample, candidate, loopMatchWindowSamples);
      if (score < bestScore) {
        bestScore = score;
        bestEndSample = candidate;
      }
    }
  }

  const zeroCrossRadius = Math.max(48, Math.floor(getLoopZeroCrossingSearchSeconds(type) * sampleRate));
  const snappedStartSample = findNearestZeroCrossing(
    channelData,
    adjustedStartSample,
    firstAudibleSample,
    Math.max(firstAudibleSample, bestEndSample - minLoopSamples),
    zeroCrossRadius
  );

  const snappedEndSample = findNearestZeroCrossing(
    channelData,
    bestEndSample,
    Math.min(lastUsableSample, snappedStartSample + minLoopSamples),
    lastUsableSample,
    zeroCrossRadius
  );

  const finalStartSample = Math.max(firstAudibleSample, snappedStartSample);
  const finalEndSample = Math.max(finalStartSample + minLoopSamples, snappedEndSample);
  if (finalEndSample >= audioBuffer.length || finalEndSample <= finalStartSample) {
    return null;
  }

  return {
    start: finalStartSample / sampleRate,
    end: finalEndSample / sampleRate,
  };
};

const getFallbackLoopPoints = (audioBuffer: AudioBuffer, type: NoiseType): LoopPoints | null => {
  const duration = audioBuffer.duration;
  if (!Number.isFinite(duration) || duration <= 2) {
    return null;
  }

  const introSkip = getLoopIntroSkipSeconds(type);
  const start = Math.max(0.2, Math.min(introSkip, Math.max(0.2, duration - 1.6)));
  const minimumLoopSeconds = getMinimumLoopSeconds(audioBuffer, type);
  const end = Math.max(start + minimumLoopSeconds, duration - getLoopTailReserveSeconds(type));

  if (end - start < minimumLoopSeconds || end >= duration) {
    return null;
  }

  return { start, end };
};

// Normalize the decoded audio before finding a loop.
const normalizeAudioBuffer = (audioBuffer: AudioBuffer) => {
  const channelCount = audioBuffer.numberOfChannels;
  const channelDataList = Array.from({ length: channelCount }, (_, index) => audioBuffer.getChannelData(index));

  // Fix for uneven stereo sources: downmix to mono so both ears get the same signal
  if (channelCount > 1) {
    const sampleLength = channelDataList[0]?.length ?? 0;
    for (let i = 0; i < sampleLength; i++) {
      let sum = 0;
      for (let ch = 0; ch < channelCount; ch++) {
        sum += channelDataList[ch]![i] ?? 0;
      }
      const monoSample = sum / channelCount;
      for (let ch = 0; ch < channelCount; ch++) {
        channelDataList[ch]![i] = monoSample;
      }
    }
  }

  const referenceChannel = channelDataList[0] ?? new Float32Array(0);
  let maxAmplitude = 0;

  // Find peak amplitude.
  for (let i = 0; i < referenceChannel.length; i++) {
    if (Math.abs(referenceChannel[i] ?? 0) > maxAmplitude) {
      maxAmplitude = Math.abs(referenceChannel[i] ?? 0);
    }
  }

  // Normalize amplitude.
  if (maxAmplitude > 0) {
    const scaleFactor = 1 / maxAmplitude;
    for (let ch = 0; ch < channelCount; ch++) {
      const channelData = channelDataList[ch] ?? new Float32Array(0);
      for (let i = 0; i < channelData.length; i++) {
        channelData[i] *= scaleFactor;
      }
    }
  }

  // Remove DC offset to reduce micro-clicks at loop boundaries.
  for (let ch = 0; ch < channelCount; ch++) {
    const channelData = channelDataList[ch] ?? new Float32Array(0);
    if (channelData.length === 0) continue;

    let sum = 0;
    for (let i = 0; i < channelData.length; i++) {
      sum += channelData[i] ?? 0;
    }

    const mean = sum / channelData.length;
    if (Math.abs(mean) < 1e-7) continue;

    for (let i = 0; i < channelData.length; i++) {
      channelData[i] = (channelData[i] ?? 0) - mean;
    }
  }

  return audioBuffer;
};


export function prepareAmbientLoop(buffer:AudioBuffer,type:NoiseType) {
  normalizeAudioBuffer(buffer);
  return detectLoopPoints(buffer,type) ?? getFallbackLoopPoints(buffer,type) ?? {start:0,end:buffer.duration};
}
